import { useFormContext } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { array, string, option, ord } from 'fp-ts';
import { Invoice } from '@mews-commander/data-access';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Tag } from '@optimus-web/core';
import { SelectField } from '@mews-commander/form';
import { useDefaultFormContext } from '../forms/ProcessPayment/useDefaultFormValues';
import { useGetPayableInvoicesSuspense } from './queries';
import { useCreateInvoiceLabel, useCreateInvoiceSecondaryText, useGetTagPropsForInvoice } from './hooks';

interface InvoiceAsyncSelectProps {
    name: string;
    required?: boolean;
}

const sortByDueUtc = pipe(
    string.Ord,
    ord.contramap((b: Invoice) => b.dueUtc || '')
);

export const InvoiceAsyncSelect = ({ name, required = false }: InvoiceAsyncSelectProps) => {
    const { setValue, watch } = useFormContext();
    const {
        setSelectedInvoiceIds,
        accountId,
        defaultFormValues: { invoiceIds: defaultInvoiceIds },
    } = useDefaultFormContext();

    const getTagPropsForInvoice = useGetTagPropsForInvoice();
    const createInvoiceLabel = useCreateInvoiceLabel();
    const createInvoiceSecondaryText = useCreateInvoiceSecondaryText();

    const { data } = useGetPayableInvoicesSuspense({ ownerIds: [accountId] });

    const invoiceIds = watch(name);

    const sortedInvoices = useMemo(() => pipe(data?.invoices || [], array.sortBy([sortByDueUtc])), [data]);

    useEffect(() => {
        if ((invoiceIds || []).includes('')) {
            setValue(name, []);
        }
    }, [invoiceIds, setValue, name]);

    useEffect(() => {
        if (defaultInvoiceIds?.length) {
            return;
        }

        const oldestInvoice = pipe(
            sortedInvoices,
            array.head,
            option.match(
                () => null,
                (invoice) => invoice
            )
        );

        if (oldestInvoice) {
            setValue(name, [oldestInvoice.id]);
            setSelectedInvoiceIds([oldestInvoice.id]);
        }
    }, [sortedInvoices, defaultInvoiceIds, setSelectedInvoiceIds, setValue, name]);

    return (
        <SelectField
            multiselect
            onChange={(_, v) => setSelectedInvoiceIds(v as string[])}
            name={name}
            helper={<LocalizedText textKey={TextKey.SelectInvoicesToLinkPaymentHelperMessage} />}
            required={required}
            label={<LocalizedText textKey={TextKey.SelectInvoicesToLinkPaymentLabel} />}
            placeholder={<LocalizedText textKey={TextKey.SelectInvoicesToLinkPaymentEmptyOptionLabel} />}
            options={[
                {
                    groupLabel: <LocalizedText textKey={TextKey.SelectInvoicesToLinkPaymentGroupLabel} />,
                    groupOptions: sortedInvoices.map((invoice) => ({
                        value: invoice.id,
                        disabled: false,
                        indent: false,
                        inactive: false,
                        label: createInvoiceLabel(invoice),
                        secondaryText: createInvoiceSecondaryText(invoice),
                        rightItem: <Tag {...getTagPropsForInvoice(invoice)} />,
                    })),
                },
            ]}
        />
    );
};
